import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Label } from '@clarix/ui';
import { loginSchema } from '@clarix/utils';
import {
  useLogin,
  useLoginWithOtp,
  useRequestLoginOtp,
  getApiErrorMessage,
} from '@/core/api';
import { PasswordInput } from './PasswordInput';
import { OtpInput } from './OtpInput';
import {
  TwoFactorChallengeForm,
  EnrolTwoFactorForm,
  ChangePasswordForm,
} from './LoginGateForms';
import { useAuthStore } from '@clarix/auth-store';
import { buildWorkspaceUrl, useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { IS_SINGLE_HOST } from '@/config/env';
import { loginMode } from './loginMode';
import type { LoginResponse, LoginResult } from '@clarix/shared-types';
import {
  isTwoFactorChallenge,
  isEnrol2faRequired,
  isPasswordChangeRequired,
  isPasswordResetRequired,
} from '@clarix/shared-types';

/**
 * /login — both a password and an "Email code" (OTP) tab are always available;
 * only the default tab depends on the URL context:
 *   • Apex (no workspace slug)  → defaults to password (owners/admins), with the
 *                                  code tab for invited members.
 *   • Workspace subdomain       → defaults to the emailed code (members), with a
 *                                  password tab for anyone who set one (owners,
 *                                  admins, and invited members who chose a
 *                                  password on the invite landing page).
 *
 * Both methods finish with the same JWT-handoff via `completeLogin` (on a
 * subdomain that lands on `/`, where the AppLauncher lets the user pick an app).
 */

/** Resend cooldown: 1 minute after the first send, 10 minutes after a resend. */
const OTP_FIRST_RESEND_DELAY_SECONDS = 60;
const OTP_RESEND_DELAY_SECONDS = 10 * 60;

/** Formats a countdown as M:SS. */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** The follow-up step a gated login result parks on (null = normal sign-in). */
type Gate =
  | null
  | { kind: '2fa'; challengeToken: string }
  | { kind: 'enrol'; token: string; workspaceSlug: string | null; canSkip: boolean }
  | { kind: 'password'; token: string; reason: 'POLICY' | 'ROTATION'; workspaceSlug: string | null };

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get('next');
  // Shareable prefill: /login?email=…&password=… drops straight into a filled
  // password form (the credentials are only prefilled — the user still submits).
  const prefillEmail = params.get('email') ?? '';
  const prefillPassword = params.get('password') ?? '';
  // In single-host mode there is no workspace subdomain — the slug lives in the
  // session (null until signed in), so the apex password login is always shown
  // (owners), with the "Email code" tab available for OTP-only members.
  const setWorkspaceSlug = useAuthStore((s) => s.setWorkspaceSlug);
  const slug = useActiveWorkspaceSlug();
  const mode = loginMode(slug);
  // Both methods are always offered; the URL context only picks the initial tab.
  // Apex → password (owners/admins); workspace subdomain → emailed code (members,
  // who may still switch to a password they set on the invite landing page).
  // A `?password=` in the link is an explicit ask for the password form, so it
  // wins over the slug default (a subdomain would otherwise open the code tab
  // and hide the prefill).
  const [method, setMethod] = useState<'password' | 'code'>(
    prefillPassword || mode === 'password' ? 'password' : 'code',
  );

  const login = useLogin();
  const requestOtp = useRequestLoginOtp();
  const loginOtp = useLoginWithOtp();

  // A gated login (2FA / forced enrolment / stale password) parks the flow on a
  // follow-up step until it resolves to a full session. `notice` carries a
  // one-line status back to the sign-in form (e.g. after a forced change).
  const [gate, setGate] = useState<Gate>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const completeLogin = (data: LoginResponse): void => {
    // Single-host mode: one origin, no cross-subdomain redirect. The full session
    // was already persisted by the login mutation, so just navigate in-SPA — a
    // workspace-routed login lands on '/' (or ?next), everything else onboards.
    if (IS_SINGLE_HOST) {
      const routed = data.next_step === 'ROUTE_TO_WORKSPACE' && !!data.workspace;
      navigate(routed ? (nextPath ? decodeURIComponent(nextPath) : '/') : '/onboarding', {
        replace: true,
      });
      return;
    }
    if (data.next_step !== 'ROUTE_TO_WORKSPACE' || !data.workspace) {
      // On a workspace subdomain the workspace already exists — land on '/'
      // and let WorkspaceShell's /me membership check route to the first
      // enabled app (or bounce non-members). Onboarding is apex-only.
      navigate(slug ? '/' : '/onboarding', { replace: true });
      return;
    }
    const target = buildWorkspaceUrl(
      data.workspace.slug,
      nextPath ? decodeURIComponent(nextPath) : '/',
    );
    const targetWithToken = `${target}#token=${encodeURIComponent(data.access_token)}`;
    window.location.replace(targetWithToken);
  };

  // Route a login result: a full session completes; a gated result parks on its
  // follow-up step. Reused by both password and OTP sign-in, and by the 2FA
  // challenge step (verify-login can itself return a password-change gate).
  const routeResult = (data: LoginResult): void => {
    if (isTwoFactorChallenge(data)) {
      setGate({ kind: '2fa', challengeToken: data.challenge_token });
      return;
    }
    if (isEnrol2faRequired(data)) {
      setGate({
        kind: 'enrol',
        token: data.access_token,
        workspaceSlug: data.workspace_slug,
        // Absent (older API build) → treat as compulsory. Fails closed: the worst
        // case is we hide a skip the user was entitled to, never the reverse.
        canSkip: data.can_skip === true,
      });
      return;
    }
    if (isPasswordChangeRequired(data)) {
      setGate({
        kind: 'password',
        token: data.access_token,
        reason: data.reason,
        workspaceSlug: data.workspace_slug,
      });
      return;
    }
    if (isPasswordResetRequired(data)) {
      navigate('/password-reset-required', { replace: true });
      return;
    }
    completeLogin(data);
  };

  if (gate) {
    return (
      <div>
        {gate.kind === '2fa' && (
          <TwoFactorChallengeForm
            challengeToken={gate.challengeToken}
            workspaceSlug={slug}
            onResult={routeResult}
            onBack={() => setGate(null)}
          />
        )}
        {gate.kind === 'enrol' && (
          <EnrolTwoFactorForm
            token={gate.token}
            workspaceSlug={gate.workspaceSlug}
            canSkip={gate.canSkip}
            onComplete={completeLogin}
          />
        )}
        {gate.kind === 'password' && (
          <ChangePasswordForm
            token={gate.token}
            reason={gate.reason}
            workspaceSlug={gate.workspaceSlug}
            onChanged={() => {
              setGate(null);
              setNotice('Password updated. Please sign in with your new password.');
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-5xl font-semibold text-slate-900 dark:text-slate-100">
          Sign in to Clarix
        </h1>
        <p className="mt-1 text-xl text-slate-500">
          {method === 'password'
            ? 'Welcome back to Clarix, sign in to continue.'
            : 'Enter the code we email you to sign in.'}
        </p>
      </div>

      {notice && (
        <div className="mb-5 rounded-md bg-emerald-50 px-3 py-2 text-xl text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
          {notice}
        </div>
      )}

      {/* Single-host only: on a subdomain deployment the slug IS the URL, so
          "changing workspace" means navigating to a different origin — nothing
          to fix here. In single-host mode the slug lives only in the persisted
          auth store (WorkspacePickerPage's setWorkspaceSlug), and once set,
          LoginRoute skips the picker on every future visit to /login with no
          way back — so a stale slug from an earlier attempt silently keeps
          resolving login against the WRONG workspace with no error surfaced. */}
      {IS_SINGLE_HOST && slug && (
        <p className="mb-5 text-xl text-slate-500">
          Workspace: <span className="font-medium text-slate-900 dark:text-slate-100">{slug}</span>.{' '}
          <button
            type="button"
            onClick={() => {
              setWorkspaceSlug(null);
              navigate('/login', { replace: true });
            }}
            className="text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Change workspace
          </button>
        </p>
      )}

      <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
        <button
          type="button"
          onClick={() => setMethod('password')}
          className={`rounded-md px-3 py-1.5 text-xl font-medium transition ${
            method === 'password'
              ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-slate-100'
              : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          Password
        </button>
        <button
          type="button"
          onClick={() => setMethod('code')}
          className={`rounded-md px-3 py-1.5 text-xl font-medium transition ${
            method === 'code'
              ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-slate-100'
              : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          Email code
        </button>
      </div>
      {method === 'password' ? (
        <PasswordForm
          login={login}
          workspaceSlug={slug}
          initialEmail={prefillEmail}
          initialPassword={prefillPassword}
          onSuccess={routeResult}
        />
      ) : (
        <OtpForm
          requestOtp={requestOtp}
          loginOtp={loginOtp}
          onSuccess={routeResult}
          initialEmail={prefillEmail}
          workspaceSlug={slug}
        />
      )}

      {/* Apex only — signing up is how a brand-new workspace starts, and that
          flow (signup → verify email → workspace details → login) is
          apex-bound. On a workspace subdomain people join an existing
          workspace by invite, so offering "create an account" there would
          only misroute them. */}
      {!slug && (
        <p className="mt-6 text-center text-xl text-slate-500">
          New to Clarix?{' '}
          <Link to="/signup" className="font-medium text-indigo-600 hover:underline">
            Create an account
          </Link>
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function PasswordForm({
  login,
  workspaceSlug,
  initialEmail,
  initialPassword,
  onSuccess,
}: {
  login: ReturnType<typeof useLogin>;
  workspaceSlug?: string | null;
  initialEmail?: string;
  initialPassword?: string;
  onSuccess: (data: LoginResult) => void;
}) {
  const [form, setForm] = useState({
    email: initialEmail ?? '',
    password: initialPassword ?? '',
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'email' | 'password', string>>>({});

  const clearFieldError = (field: 'email' | 'password') =>
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate client-side first so empty/invalid input gets a specific inline
    // message instead of the server's opaque "Invalid request payload".
    const parsed = loginSchema.safeParse(form);
    if (!parsed.success) {
      const next: Partial<Record<'email' | 'password', string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as 'email' | 'password';
        if (key && !next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    // The slug is ALWAYS sent: login is workspace-scoped, and the backend
    // rejects a request without one (400 WORKSPACE_REQUIRED). It comes from the
    // subdomain, or from the session in single-host mode — never from user input
    // on this form. LoginRoute guarantees one exists before rendering us.
    try {
      const data = await login.mutateAsync({
        ...form,
        workspace_slug: workspaceSlug ?? '',
      });
      onSuccess(data);
    } catch {
      /* surfaced below */
    }
  };

  // Show the server's real message for genuine failures (e.g. 401 wrong
  // credentials, "Account has no password set"), with a friendly fallback for
  // opaque/network errors instead of raw Axios text.
  const serverError = login.error
    ? getApiErrorMessage(login.error, "Couldn't sign in. Check your email and password and try again.")
    : null;

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@company.com"
          value={form.email}
          aria-invalid={!!fieldErrors.email || undefined}
          onChange={(e) => {
            setForm((f) => ({ ...f, email: e.target.value }));
            clearFieldError('email');
          }}
        />
        {fieldErrors.email && (
          <p className="mt-1 text-lg text-red-600 dark:text-red-400">{fieldErrors.email}</p>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Link
            to="/forgot-password"
            className="text-lg font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
          >
            Forgot password?
          </Link>
        </div>
        <PasswordInput
          id="password"
          name="password"
          autoComplete="current-password"
          required
          placeholder="Enter your password"
          value={form.password}
          aria-invalid={!!fieldErrors.password || undefined}
          onChange={(e) => {
            setForm((f) => ({ ...f, password: e.target.value }));
            clearFieldError('password');
          }}
        />
        {fieldErrors.password && (
          <p className="mt-1 text-lg text-red-600 dark:text-red-400">{fieldErrors.password}</p>
        )}
      </div>

      {serverError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {serverError}
        </div>
      )}

      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}

// -----------------------------------------------------------------------------

function OtpForm({
  requestOtp,
  loginOtp,
  onSuccess,
  initialEmail,
  workspaceSlug,
}: {
  requestOtp: ReturnType<typeof useRequestLoginOtp>;
  loginOtp: ReturnType<typeof useLoginWithOtp>;
  onSuccess: (data: LoginResult) => void;
  initialEmail?: string;
  workspaceSlug?: string | null;
}) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [code, setCode] = useState('');
  // Countdown until the emailed code expires; Resend appears only once it hits 0.
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const onRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await requestOtp.mutateAsync({ email, ...(workspaceSlug ? { workspace_slug: workspaceSlug } : {}) });
      setStep('code');
      setSecondsLeft(OTP_FIRST_RESEND_DELAY_SECONDS);
    } catch {
      /* surfaced below */
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = await loginOtp.mutateAsync({
        email,
        code,
        workspace_slug: workspaceSlug ?? '',
      });
      onSuccess(data);
    } catch {
      /* surfaced below */
    }
  };

  const onResend = async () => {
    try {
      await requestOtp.mutateAsync({ email, ...(workspaceSlug ? { workspace_slug: workspaceSlug } : {}) });
      setCode('');
      setSecondsLeft(OTP_RESEND_DELAY_SECONDS);
    } catch {
      /* surfaced inline */
    }
  };

  // Show an informative, user-friendly message for every failure mode — the
  // server's message for quota/rate-limit (429 "Too many OTP requests…"), a
  // clear network-error string when the request never reached the server, and a
  // sensible fallback for opaque/empty responses. getApiErrorMessage handles
  // all three (raw normalizeError(...).message leaked Axios text like "Network
  // Error" and blanked on opaque payloads).
  const requestErr = requestOtp.error
    ? getApiErrorMessage(requestOtp.error, "Couldn't send the code. Please try again.")
    : null;
  const verifyErr = loginOtp.error
    ? getApiErrorMessage(loginOtp.error, "Couldn't verify the code. Please try again.")
    : null;

  if (step === 'email') {
    return (
      <form className="space-y-4" onSubmit={onRequestCode} noValidate>
        <div>
          <Label htmlFor="otp-email">Email</Label>
          <Input
            id="otp-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <p className="mt-1 text-lg text-slate-500">
            We&apos;ll email you a 6-digit code. No password needed.
          </p>
        </div>

        {requestErr && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {requestErr}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={requestOtp.isPending}>
          {requestOtp.isPending ? 'Sending code…' : 'Send code'}
        </Button>
      </form>
    );
  }

  return (
    <form className="space-y-4" onSubmit={onVerify} noValidate>
      <p className="text-xl text-slate-600 dark:text-slate-400">
        Code sent to <span className="font-bold text-slate-900 dark:text-slate-100">{email}</span>.{' '}
        <button
          type="button"
          onClick={() => {
            setStep('email');
            setCode('');
            loginOtp.reset();
            requestOtp.reset();
          }}
          className="text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Change email
        </button>
      </p>

      <div>
        <Label htmlFor="otp-code">6-digit code</Label>
        <div className="mt-2">
          <OtpInput
            value={code}
            onChange={setCode}
            disabled={loginOtp.isPending}
            ariaLabel="6-digit code"
          />
        </div>
        {secondsLeft > 0 ? (
          <p className="mt-2 text-lg text-slate-500">
            Didn&apos;t get the code? You can resend in{' '}
            <span className="font-medium tabular-nums text-slate-700 dark:text-slate-300">
              {formatCountdown(secondsLeft)}
            </span>
            .
          </p>
        ) : (
          <p className="mt-2 text-lg text-slate-500">
            Didn&apos;t get the code?{' '}
            <button
              type="button"
              onClick={onResend}
              disabled={requestOtp.isPending}
              className="font-medium text-indigo-600 hover:underline disabled:text-slate-400 dark:text-indigo-400"
            >
              {requestOtp.isPending ? 'Sending…' : 'Resend code'}
            </button>
          </p>
        )}
      </div>

      {verifyErr && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {verifyErr}
        </div>
      )}

      <Button type="submit" className="w-full" disabled={loginOtp.isPending || code.length !== 6}>
        {loginOtp.isPending ? 'Verifying…' : 'Sign in'}
      </Button>
    </form>
  );
}
